// Replaces the native browser confirm() that Turbo triggers for every
// `data-turbo-confirm` action with the themed dialog rendered by
// backoffice/shared/_global_confirm_dialog. One hook covers every confirm
// site, so individual views need no changes.
//
// Opt-in styling per action: add `data: { confirm_variant: "primary" }`
// (alongside turbo_confirm) on the link/button. Defaults to "danger" since
// the overwhelming majority of confirmed actions are destructive.
import i18n from "./util/i18n"

const ICONS = {
  danger: `<i class="fa-solid fa-triangle-exclamation text-2xl text-error-600 dark:text-error-400"></i>`,
  primary: `<i class="fa-solid fa-circle-info text-2xl text-brand-600 dark:text-brand-400"></i>`,
}

const VARIANTS = {
  danger: {
    accept: ["bg-error-600", "hover:bg-error-700", "focus:ring-error-200"],
    iconWrap: ["bg-error-50", "dark:bg-error-500/10"],
  },
  primary: {
    accept: ["bg-brand-500", "hover:bg-brand-600", "focus:ring-brand-200"],
    iconWrap: ["bg-brand-50", "dark:bg-brand-500/10"],
  },
}

function applyVariant(dialog, name) {
  const variant = VARIANTS[name] || VARIANTS.danger
  const accept = dialog.querySelector("[data-confirm-accept]")
  const iconWrap = dialog.querySelector("[data-confirm-icon]")

  accept.className =
    "inline-flex flex-1 items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-theme-xs transition-colors focus:outline-none focus:ring-2"
  accept.classList.add(...variant.accept)

  iconWrap.className =
    "mx-auto flex h-12 w-12 items-center justify-center rounded-full"
  iconWrap.classList.add(...variant.iconWrap)
  iconWrap.innerHTML = ICONS[name] || ICONS.danger
}

function confirmWithDialog(message, formElement, submitter) {
  const dialog = document.getElementById("global-confirm-dialog")

  // Fail safe: if the dialog isn't on the page, fall back to native confirm
  // so a destructive action is never silently auto-confirmed.
  if (!dialog || typeof dialog.showModal !== "function") {
    // native-dialog-ok: last resort when the themed <dialog> is absent — the
    // alternative is auto-confirming a destructive action without asking.
    return Promise.resolve(window.confirm(message))
  }

  const messageEl = dialog.querySelector("[data-confirm-message]")
  const accept = dialog.querySelector("[data-confirm-accept]")
  const cancel = dialog.querySelector("[data-confirm-cancel]")

  messageEl.textContent = message || messageEl.dataset.default || i18n.t("js.common.are_you_sure")

  const variant =
    submitter?.dataset.confirmVariant ||
    formElement?.dataset.confirmVariant ||
    "danger"
  applyVariant(dialog, variant)

  return new Promise((resolve) => {
    let settled = false

    const finish = (result) => {
      if (settled) return
      settled = true
      accept.removeEventListener("click", onAccept)
      cancel.removeEventListener("click", onCancel)
      dialog.removeEventListener("click", onBackdrop)
      dialog.removeEventListener("close", onClose)
      if (dialog.open) dialog.close()
      resolve(result)
    }

    const onAccept = () => finish(true)
    const onCancel = () => finish(false)
    const onClose = () => finish(false) // Esc key, etc.
    const onBackdrop = (event) => {
      if (event.target === dialog) finish(false)
    }

    accept.addEventListener("click", onAccept)
    cancel.addEventListener("click", onCancel)
    dialog.addEventListener("click", onBackdrop)
    dialog.addEventListener("close", onClose)

    dialog.showModal()
    accept.focus()
  })
}

export function setupConfirmDialog() {
  // Alpine confirms the reference paste through the same themed dialog rather
  // than a native confirm(); Turbo only reaches it via data-turbo-confirm.
  window.boConfirm = (message, variant = "primary") =>
    confirmWithDialog(message, null, { dataset: { confirmVariant: variant } })

  const Turbo = window.Turbo
  if (!Turbo) return

  // Turbo 8 exposes config.forms.confirm; fall back to the legacy setter.
  if (Turbo.config?.forms) {
    Turbo.config.forms.confirm = confirmWithDialog
  } else if (typeof Turbo.setConfirmMethod === "function") {
    Turbo.setConfirmMethod(confirmWithDialog)
  }
}
